import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { API_BASE_URL } from '../config';
import secureStorage from '../utils/secureStorage';

// Module-level API helpers (stable references, never change)
const medApi = {
  getStations: (exId) => axios.get(`${API_BASE_URL}/api/ics-206/med-aid-stations`, { params: { exercise_id: exId } }),
  saveStations: (exId, data) => axios.put(`${API_BASE_URL}/api/ics-206/med-aid-stations/${exId}`, data),
  getTransport: (exId) => axios.get(`${API_BASE_URL}/api/ics-206/med-transport`, { params: { exercise_id: exId } }),
  saveTransport: (exId, data) => axios.put(`${API_BASE_URL}/api/ics-206/med-transport/${exId}`, data),
  getHospitals: (exId) => axios.get(`${API_BASE_URL}/api/ics-206/hospitals`, { params: { exercise_id: exId } }),
  saveHospitals: (exId, data) => axios.put(`${API_BASE_URL}/api/ics-206/hospitals/${exId}`, data),
  getProcedures: (exId) => axios.get(`${API_BASE_URL}/api/ics-206/med-procedures`, { params: { exercise_id: exId } }),
  saveProcedures: (exId, data) => axios.put(`${API_BASE_URL}/api/ics-206/med-procedures/${exId}`, data),
  getPlanEntries: (exId) => axios.get(`${API_BASE_URL}/api/ics-206/med-plan-entries`, { params: { exercise_id: exId } }),
  savePlanEntries: (exId, data) => axios.put(`${API_BASE_URL}/api/ics-206/med-plan-entries/${exId}`, data),
};

const notify = (msg, type = 'success') => type === 'error' ? toast.error(msg) : toast.success(msg);

/**
 * Custom hook for managing ICS 206 Medical Plan state
 * With real-time sync support
 * Includes: Medical Aid Stations, Medical Transport, Hospitals, Medical Procedures
 */
const useMedicalPlan = (currentExercise) => {
  // ========== Shared Sync State ==========
  const [loading, setLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState({
    medAid: null,
    transport: null,
    hospitals: null,
    procedures: null
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState({
    medAid: false,
    transport: false,
    hospitals: false,
    procedures: false
  });

  const pollingIntervalRef = useRef(null);

  // ========== Medical Aid Stations ==========
  const getDefaultMedAidStation = () => ({
    station: '',
    location: '',
    contact: '',
    paramedics: 'No'
  });

  const [medAidStations, setMedAidStations] = useState([]);
  const [currentMedAidStation, setCurrentMedAidStation] = useState(getDefaultMedAidStation());
  const [editingMedAidStation, setEditingMedAidStation] = useState(null);

  // Load Med Aid Stations
  const loadMedAidStations = useCallback(async (silent = false) => {
    if (!currentExercise?.id) return;
    try {
      if (!silent) setLoading(true);
      const response = await medApi.getStations(currentExercise.id);
      if (response.data && response.data.stations) {
        setMedAidStations(response.data.stations);
        setLastSaved(prev => ({ ...prev, medAid: response.data.updated_at }));
      }
    } catch (error) {
      if (error.response?.status !== 404 && !silent) {
        console.error('Error loading Med Aid Stations:', error);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [currentExercise?.id]);

  // Save Med Aid Stations
  const saveMedAidStations = async () => {
    if (!currentExercise?.id) {
      notify('No exercise selected');
      return;
    }
    try {
      setLoading(true);
      await medApi.saveStations(currentExercise.id, {
        stations: medAidStations,
        exercise_name: currentExercise.exercise_name
      });
      setLastSaved(prev => ({ ...prev, medAid: new Date().toISOString() }));
      setHasUnsavedChanges(prev => ({ ...prev, medAid: false }));
      notify('Medical Aid Stations saved');
    } catch (error) {
      console.error('Error saving Med Aid Stations:', error);
      notify('Failed to save Medical Aid Stations');
    } finally {
      setLoading(false);
    }
  };

  const addMedAidStation = () => {
    if (!currentMedAidStation.station) {
      notify('Please enter a station name');
      return;
    }
    
    let updatedStations;
    if (editingMedAidStation) {
      updatedStations = medAidStations.map(s => 
        s.id === editingMedAidStation.id 
          ? { ...currentMedAidStation, id: s.id, timestamp: s.timestamp, updatedAt: new Date().toISOString() }
          : s
      );
      setEditingMedAidStation(null);
      notify('Station updated');
    } else {
      const newStation = {
        ...currentMedAidStation,
        id: Date.now().toString(),
        timestamp: new Date().toISOString()
      };
      updatedStations = [...medAidStations, newStation];
      notify('Station added');
    }
    
    setMedAidStations(updatedStations);
    setCurrentMedAidStation(getDefaultMedAidStation());
    // Auto-save
    if (currentExercise?.id) {
      medApi.saveStations(currentExercise.id, { stations: updatedStations, exercise_name: currentExercise.exercise_name }).catch(e => console.error('Auto-save failed:', e));
    }
  };

  const editMedAidStation = (station) => {
    setCurrentMedAidStation({
      station: station.station || '',
      location: station.location || '',
      contact: station.contact || '',
      paramedics: station.paramedics || 'No'
    });
    setEditingMedAidStation(station);
  };

  const cancelEditMedAidStation = () => {
    setCurrentMedAidStation(getDefaultMedAidStation());
    setEditingMedAidStation(null);
  };

  const deleteMedAidStation = (stationId) => {
    if (window.confirm('Are you sure you want to delete this medical aid station?')) {
      const updatedStations = medAidStations.filter(s => s.id !== stationId);
      setMedAidStations(updatedStations);
      notify('Station deleted');
      if (currentExercise?.id) {
        medApi.saveStations(currentExercise.id, { stations: updatedStations, exercise_name: currentExercise.exercise_name }).catch(e => console.error('Auto-save failed:', e));
      }
    }
  };

  // ========== Medical Transport ==========
  const getDefaultMedTransport = () => ({
    ambulanceService: '',
    location: '',
    contact: '',
    levelOfService: '',
    airGround: 'Ground'
  });

  const [medTransports, setMedTransports] = useState([]);
  const [currentMedTransport, setCurrentMedTransport] = useState(getDefaultMedTransport());
  const [editingMedTransport, setEditingMedTransport] = useState(null);

  // Load Med Transport
  const loadMedTransport = useCallback(async (silent = false) => {
    if (!currentExercise?.id) return;
    try {
      if (!silent) setLoading(true);
      const response = await medApi.getTransport(currentExercise.id);
      if (response.data && response.data.transports) {
        setMedTransports(response.data.transports);
        setLastSaved(prev => ({ ...prev, transport: response.data.updated_at }));
      }
    } catch (error) {
      if (error.response?.status !== 404 && !silent) {
        console.error('Error loading Med Transport:', error);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [currentExercise?.id]);

  // Save Med Transport
  const saveMedTransport = async () => {
    if (!currentExercise?.id) {
      notify('No exercise selected');
      return;
    }
    try {
      setLoading(true);
      await medApi.saveTransport(currentExercise.id, {
        transports: medTransports,
        exercise_name: currentExercise.exercise_name
      });
      setLastSaved(prev => ({ ...prev, transport: new Date().toISOString() }));
      setHasUnsavedChanges(prev => ({ ...prev, transport: false }));
      notify('Medical Transport saved');
    } catch (error) {
      console.error('Error saving Med Transport:', error);
      notify('Failed to save Medical Transport');
    } finally {
      setLoading(false);
    }
  };

  const addMedTransport = () => {
    if (!currentMedTransport.ambulanceService) {
      notify('Please enter an ambulance service name');
      return;
    }
    
    let updatedTransports;
    if (editingMedTransport) {
      updatedTransports = medTransports.map(t => 
        t.id === editingMedTransport.id 
          ? { ...currentMedTransport, id: t.id, timestamp: t.timestamp, updatedAt: new Date().toISOString() }
          : t
      );
      setEditingMedTransport(null);
      notify('Transport updated');
    } else {
      const newTransport = {
        ...currentMedTransport,
        id: Date.now().toString(),
        timestamp: new Date().toISOString()
      };
      updatedTransports = [...medTransports, newTransport];
      notify('Transport added');
    }
    
    setMedTransports(updatedTransports);
    setCurrentMedTransport(getDefaultMedTransport());
    if (currentExercise?.id) {
      medApi.saveTransport(currentExercise.id, { transports: updatedTransports, exercise_name: currentExercise.exercise_name }).catch(e => console.error('Auto-save failed:', e));
    }
  };

  const editMedTransport = (transport) => {
    setCurrentMedTransport({
      ambulanceService: transport.ambulanceService || '',
      location: transport.location || '',
      contact: transport.contact || '',
      levelOfService: transport.levelOfService || '',
      airGround: transport.airGround || 'Ground'
    });
    setEditingMedTransport(transport);
  };

  const cancelEditMedTransport = () => {
    setCurrentMedTransport(getDefaultMedTransport());
    setEditingMedTransport(null);
  };

  const deleteMedTransport = (transportId) => {
    if (window.confirm('Are you sure you want to delete this transport service?')) {
      const updatedTransports = medTransports.filter(t => t.id !== transportId);
      setMedTransports(updatedTransports);
      notify('Transport deleted');
      if (currentExercise?.id) {
        medApi.saveTransport(currentExercise.id, { transports: updatedTransports, exercise_name: currentExercise.exercise_name }).catch(e => console.error('Auto-save failed:', e));
      }
    }
  };

  // ========== Hospitals ==========
  const getDefaultHospital = () => ({
    hospitalName: '',
    address: '',
    contactNumber: '',
    travelTime: '',
    helipad: 'No',
    burnCenter: 'No',
    traumaCenter: 'No',
    traumaLevel: ''
  });

  const [hospitals, setHospitals] = useState([]);
  const [currentHospital, setCurrentHospital] = useState(getDefaultHospital());
  const [editingHospital, setEditingHospital] = useState(null);
  const [hospitalValidation, setHospitalValidation] = useState({});

  // Load Hospitals
  const loadHospitals = useCallback(async (silent = false) => {
    if (!currentExercise?.id) return;
    try {
      if (!silent) setLoading(true);
      const response = await medApi.getHospitals(currentExercise.id);
      if (response.data && response.data.hospitals) {
        setHospitals(response.data.hospitals);
        setLastSaved(prev => ({ ...prev, hospitals: response.data.updated_at }));
      }
    } catch (error) {
      if (error.response?.status !== 404 && !silent) {
        console.error('Error loading Hospitals:', error);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [currentExercise?.id]);

  // Save Hospitals
  const saveHospitals = async () => {
    if (!currentExercise?.id) {
      notify('No exercise selected');
      return;
    }
    try {
      setLoading(true);
      await medApi.saveHospitals(currentExercise.id, {
        hospitals: hospitals,
        exercise_name: currentExercise.exercise_name
      });
      setLastSaved(prev => ({ ...prev, hospitals: new Date().toISOString() }));
      setHasUnsavedChanges(prev => ({ ...prev, hospitals: false }));
      notify('Hospitals saved');
    } catch (error) {
      console.error('Error saving Hospitals:', error);
      notify('Failed to save Hospitals');
    } finally {
      setLoading(false);
    }
  };

  const addHospital = () => {
    if (!currentHospital.hospitalName) {
      setHospitalValidation({ hospitalName: 'Hospital name is required' });
      return;
    }
    
    setHospitalValidation({});
    
    let updatedHospitals;
    if (editingHospital) {
      updatedHospitals = hospitals.map(h => 
        h.id === editingHospital.id 
          ? { ...currentHospital, id: h.id, timestamp: h.timestamp, updatedAt: new Date().toISOString() }
          : h
      );
      setEditingHospital(null);
      notify('Hospital updated');
    } else {
      const newHospital = {
        ...currentHospital,
        id: Date.now().toString(),
        timestamp: new Date().toISOString()
      };
      updatedHospitals = [...hospitals, newHospital];
      notify('Hospital added');
    }
    
    setHospitals(updatedHospitals);
    setCurrentHospital(getDefaultHospital());
    if (currentExercise?.id) {
      medApi.saveHospitals(currentExercise.id, { hospitals: updatedHospitals, exercise_name: currentExercise.exercise_name }).catch(e => console.error('Auto-save failed:', e));
    }
  };

  const editHospital = (hospital) => {
    setCurrentHospital({
      hospitalName: hospital.hospitalName || '',
      address: hospital.address || '',
      contactNumber: hospital.contactNumber || '',
      travelTime: hospital.travelTime || '',
      helipad: hospital.helipad || 'No',
      burnCenter: hospital.burnCenter || 'No',
      traumaCenter: hospital.traumaCenter || 'No',
      traumaLevel: hospital.traumaLevel || ''
    });
    setEditingHospital(hospital);
    setHospitalValidation({});
  };

  const cancelEditHospital = () => {
    setCurrentHospital(getDefaultHospital());
    setEditingHospital(null);
    setHospitalValidation({});
  };

  const deleteHospital = (hospitalId) => {
    if (window.confirm('Are you sure you want to delete this hospital?')) {
      const updatedHospitals = hospitals.filter(h => h.id !== hospitalId);
      setHospitals(updatedHospitals);
      notify('Hospital deleted');
      if (currentExercise?.id) {
        medApi.saveHospitals(currentExercise.id, { hospitals: updatedHospitals, exercise_name: currentExercise.exercise_name }).catch(e => console.error('Auto-save failed:', e));
      }
    }
  };

  // ========== Medical Procedures ==========
  const getDefaultMedProcedure = () => ({
    procedureType: '',
    procedureDescription: '',
    instructions: '',
    responsibleParty: ''
  });

  const [medProcedures, setMedProcedures] = useState([]);
  const [currentMedProcedure, setCurrentMedProcedure] = useState(getDefaultMedProcedure());
  const [editingMedProcedure, setEditingMedProcedure] = useState(null);

  // Load Med Procedures
  const loadMedProcedures = useCallback(async (silent = false) => {
    if (!currentExercise?.id) return;
    try {
      if (!silent) setLoading(true);
      const response = await medApi.getProcedures(currentExercise.id);
      if (response.data && response.data.procedures) {
        setMedProcedures(response.data.procedures);
        setLastSaved(prev => ({ ...prev, procedures: response.data.updated_at }));
      }
    } catch (error) {
      if (error.response?.status !== 404 && !silent) {
        console.error('Error loading Med Procedures:', error);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [currentExercise?.id]);

  // Save Med Procedures
  const saveMedProcedures = async () => {
    if (!currentExercise?.id) {
      notify('No exercise selected');
      return;
    }
    try {
      setLoading(true);
      await medApi.saveProcedures(currentExercise.id, {
        procedures: medProcedures,
        exercise_name: currentExercise.exercise_name
      });
      setLastSaved(prev => ({ ...prev, procedures: new Date().toISOString() }));
      setHasUnsavedChanges(prev => ({ ...prev, procedures: false }));
      notify('Medical Procedures saved');
    } catch (error) {
      console.error('Error saving Med Procedures:', error);
      notify('Failed to save Medical Procedures');
    } finally {
      setLoading(false);
    }
  };

  const addMedProcedure = () => {
    if (!currentMedProcedure.procedureType) {
      notify('Please select a procedure type');
      return;
    }
    
    if (editingMedProcedure) {
      setMedProcedures(prev => prev.map(p => 
        p.id === editingMedProcedure.id 
          ? { ...currentMedProcedure, id: p.id, timestamp: p.timestamp, updatedAt: new Date().toISOString() }
          : p
      ));
      setEditingMedProcedure(null);
      notify('Procedure updated');
    } else {
      const newProcedure = {
        ...currentMedProcedure,
        id: Date.now().toString(),
        timestamp: new Date().toISOString()
      };
      setMedProcedures(prev => [...prev, newProcedure]);
      notify('Procedure added');
    }
    
    setHasUnsavedChanges(prev => ({ ...prev, procedures: true }));
    setCurrentMedProcedure(getDefaultMedProcedure());
  };

  const editMedProcedure = (procedure) => {
    setCurrentMedProcedure({
      procedureType: procedure.procedureType || '',
      procedureDescription: procedure.procedureDescription || '',
      instructions: procedure.instructions || '',
      responsibleParty: procedure.responsibleParty || ''
    });
    setEditingMedProcedure(procedure);
  };

  const cancelEditMedProcedure = () => {
    setCurrentMedProcedure(getDefaultMedProcedure());
    setEditingMedProcedure(null);
  };

  const deleteMedProcedure = (procedureId) => {
    if (window.confirm('Are you sure you want to delete this procedure?')) {
      setMedProcedures(prev => prev.filter(p => p.id !== procedureId));
      setHasUnsavedChanges(prev => ({ ...prev, procedures: true }));
      notify('Procedure deleted');
    }
  };

  // ========== Medical Plan Entries ==========
  // Each entry has: title, fromDateTime, toDateTime, plan
  const getDefaultMedPlanEntry = () => ({
    title: '',
    fromDateTime: '',
    toDateTime: '',
    plan: ''
  });

  const [medPlanEntries, setMedPlanEntries] = useState([]);
  const [currentMedPlanEntry, setCurrentMedPlanEntry] = useState(getDefaultMedPlanEntry());
  const [editingMedPlanEntry, setEditingMedPlanEntry] = useState(null);

  const loadMedPlanEntries = useCallback(async (silent = false) => {
    if (!currentExercise?.id) return;
    try {
      if (!silent) setLoading(true);
      const response = await medApi.getPlanEntries(currentExercise.id);
      if (response.data && Array.isArray(response.data.entries)) {
        setMedPlanEntries(response.data.entries);
        setLastSaved(prev => ({ ...prev, planEntries: response.data.updated_at }));
      }
    } catch (error) {
      if (error.response?.status !== 404 && !silent) {
        console.error('Error loading Med Plan Entries:', error);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [currentExercise?.id]);

  const persistMedPlanEntries = (entries) => {
    if (!currentExercise?.id) return;
    medApi.savePlanEntries(currentExercise.id, {
      entries,
      exercise_name: currentExercise.exercise_name
    }).catch(e => console.error('Auto-save Med Plan Entries failed:', e));
  };

  const addMedPlanEntry = () => {
    if (!currentMedPlanEntry.title?.trim()) {
      notify('Please enter a title', 'error');
      return;
    }
    if (currentMedPlanEntry.fromDateTime && currentMedPlanEntry.toDateTime
        && currentMedPlanEntry.toDateTime < currentMedPlanEntry.fromDateTime) {
      notify('"To" date/time must be after "From" date/time', 'error');
      return;
    }

    let updatedEntries;
    if (editingMedPlanEntry) {
      updatedEntries = medPlanEntries.map(e =>
        e.id === editingMedPlanEntry.id
          ? { ...currentMedPlanEntry, id: e.id, timestamp: e.timestamp, updatedAt: new Date().toISOString() }
          : e
      );
      setEditingMedPlanEntry(null);
      notify('Medical plan entry updated');
    } else {
      const newEntry = {
        ...currentMedPlanEntry,
        id: Date.now().toString(),
        timestamp: new Date().toISOString()
      };
      updatedEntries = [...medPlanEntries, newEntry];
      notify('Medical plan entry added');
    }

    setMedPlanEntries(updatedEntries);
    setCurrentMedPlanEntry(getDefaultMedPlanEntry());
    persistMedPlanEntries(updatedEntries);
  };

  const editMedPlanEntry = (entry) => {
    setCurrentMedPlanEntry({
      title: entry.title || '',
      fromDateTime: entry.fromDateTime || '',
      toDateTime: entry.toDateTime || '',
      plan: entry.plan || ''
    });
    setEditingMedPlanEntry(entry);
  };

  const cancelEditMedPlanEntry = () => {
    setCurrentMedPlanEntry(getDefaultMedPlanEntry());
    setEditingMedPlanEntry(null);
  };

  const deleteMedPlanEntry = (entryId) => {
    if (!window.confirm('Delete this medical plan entry?')) return;
    const updatedEntries = medPlanEntries.filter(e => e.id !== entryId);
    setMedPlanEntries(updatedEntries);
    notify('Medical plan entry deleted');
    persistMedPlanEntries(updatedEntries);
  };

  // ========== Refresh All ==========
  const refreshAll = async () => {
    await Promise.all([
      loadMedAidStations(false),
      loadMedTransport(false),
      loadHospitals(false),
      loadMedProcedures(false)
    ]);
    setHasUnsavedChanges({ medAid: false, transport: false, hospitals: false, procedures: false });
    notify('All Medical Plan data refreshed');
  };

  // ========== Save All ==========
  const saveAll = async () => {
    await Promise.all([
      saveMedAidStations(),
      saveMedTransport(),
      saveHospitals(),
      saveMedProcedures()
    ]);
  };

  // Initial load when exercise changes
  useEffect(() => {
    if (currentExercise?.id) {
      loadMedAidStations();
      loadMedTransport();
      loadHospitals();
      loadMedProcedures();
      loadMedPlanEntries();
    }
  }, [currentExercise?.id, loadMedAidStations, loadMedTransport, loadHospitals, loadMedProcedures, loadMedPlanEntries]);

  // Polling for real-time updates
  useEffect(() => {
    if (currentExercise?.id) {
      pollingIntervalRef.current = setInterval(() => {
        loadMedAidStations(true);
        loadMedTransport(true);
        loadHospitals(true);
        loadMedProcedures(true);
        loadMedPlanEntries(true);
      }, 15000);
    }
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [currentExercise?.id, loadMedAidStations, loadMedTransport, loadHospitals, loadMedProcedures, loadMedPlanEntries]);

  return {
    // Shared state
    loading,
    lastSaved,
    hasUnsavedChanges,
    refreshAll,
    saveAll,
    
    // Medical Aid Stations
    medAidStations, setMedAidStations,
    currentMedAidStation, setCurrentMedAidStation,
    editingMedAidStation, setEditingMedAidStation,
    addMedAidStation, editMedAidStation, cancelEditMedAidStation, deleteMedAidStation,
    saveMedAidStations, loadMedAidStations,
    
    // Medical Transport
    medTransports, setMedTransports,
    currentMedTransport, setCurrentMedTransport,
    editingMedTransport, setEditingMedTransport,
    addMedTransport, editMedTransport, cancelEditMedTransport, deleteMedTransport,
    saveMedTransport, loadMedTransport,
    
    // Hospitals
    hospitals, setHospitals,
    currentHospital, setCurrentHospital,
    editingHospital, setEditingHospital,
    hospitalValidation, setHospitalValidation,
    addHospital, editHospital, cancelEditHospital, deleteHospital,
    saveHospitals, loadHospitals,
    
    // Medical Procedures
    medProcedures, setMedProcedures,
    currentMedProcedure, setCurrentMedProcedure,
    editingMedProcedure, setEditingMedProcedure,
    addMedProcedure, editMedProcedure, cancelEditMedProcedure, deleteMedProcedure,
    saveMedProcedures, loadMedProcedures,

    // Medical Plan Entries (title / from / to / plan)
    medPlanEntries, setMedPlanEntries,
    currentMedPlanEntry, setCurrentMedPlanEntry,
    editingMedPlanEntry, setEditingMedPlanEntry,
    addMedPlanEntry, editMedPlanEntry, cancelEditMedPlanEntry, deleteMedPlanEntry,
    loadMedPlanEntries,

    // Default getters
    getDefaultMedAidStation,
    getDefaultMedTransport,
    getDefaultHospital,
    getDefaultMedProcedure,
    getDefaultMedPlanEntry
  };
};

export default useMedicalPlan;
